import React from "react";
import { Zap, CheckCircle } from "lucide-react";
import redditIcon from "../../assets/images/social/reddit.svg";
import xIcon from "../../assets/images/social/x-twitter.svg";
import linkedinIcon from "../../assets/images/social/linkedin.svg";
import threadsIcon from "../../assets/images/social/threads.svg";
import quoraIcon from "../../assets/images/social/quora.svg";
import "./socialSignalCard.css";

interface SocialSignalCardProps {
  platform: "reddit" | "twitter" | "linkedIn" | "threads" | "quora";
  source: string;
  timestamp: string;
  title: string;
  content: string;
  status: string;
  intent: "High Intent" | "Discovery" | "Low Intent";
  url?: string;
  onDraftReply: () => void;
  onMarkAsReplied: () => void;
  isDraftLoading?: boolean;
}

const SocialSignalCard: React.FC<SocialSignalCardProps> = ({
  platform,
  status,
  source,
  timestamp,
  title,
  content,
  intent,
  url,
  onDraftReply,
  onMarkAsReplied,
  isDraftLoading = false,
}) => {
  const getPlatformIcon = () => {
    switch (platform) {
      case "reddit":
        return redditIcon;
      case "twitter":
        return xIcon;
      case "linkedIn":
        return linkedinIcon;
      case "threads":
        return threadsIcon;
      case "quora":
        return quoraIcon;
      default:
        return redditIcon;
    }
  };

  const getPlatformColor = () => {
    switch (platform) {
      case "reddit":
        return "#FF4500";
      case "twitter":
        return "#000000";
      case "linkedIn":
        return "#0077B5";
      case "threads":
        return "#111827";
      case "quora":
        return "#B92B27";
      default:
        return "#FF4500";
    }
  };

  const getIntentColor = () => {
    switch (intent) {
      case "High Intent":
        return "#F97316";
      case "Discovery":
        return "#6366F1";
      case "Low Intent":
        return "#9CA3AF";
      default:
        return "#9CA3AF";
    }
  };

  const handleCardClick = () => {
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const formatRelativeTime = (value: string) => {
    const parsedMillis = Number(value);
    const timestampMillis = Number.isFinite(parsedMillis)
      ? parsedMillis
      : new Date(value).getTime();

    if (!Number.isFinite(timestampMillis)) {
      return value;
    }

    const elapsedSeconds = Math.max(
      0,
      Math.floor((Date.now() - timestampMillis) / 1000)
    );

    if (elapsedSeconds < 60) return `${elapsedSeconds}s ago`;

    const elapsedMinutes = Math.floor(elapsedSeconds / 60);
    if (elapsedMinutes < 60) return `${elapsedMinutes}m ago`;

    const elapsedHours = Math.floor(elapsedMinutes / 60);
    if (elapsedHours < 24) return `${elapsedHours}h ago`;

    const elapsedDays = Math.floor(elapsedHours / 24);
    if (elapsedDays < 7) return `${elapsedDays}d ago`;

    const elapsedWeeks = Math.floor(elapsedDays / 7);
    return `${elapsedWeeks}w ago`;
  };

  return (
    <div
      className="social-signal-card"
      style={{ borderLeftColor: getPlatformColor() }}
      onClick={handleCardClick}
    >
      <div className="signal-header">
        <div className="signal-source">
          <img
            src={getPlatformIcon()}
            alt={platform}
            style={{ width: 16, height: 16 }}
          />
          <span className="source-name">{source}</span>
          <span className="signal-timestamp">• {formatRelativeTime(timestamp)}</span>
        </div>
        <span
          className="intent-badge"
          style={{
            backgroundColor: `${getIntentColor()}15`,
            color: getIntentColor(),
          }}
        >
          {intent}
        </span>
      </div>

      <h3 className="signal-title">{title}</h3>
      <p className="signal-content">{content}</p>

      <div className="signal-footer">
        <div className="signal-stats">
          {/* <div className="stat">
            <ThumbsUp size={14} />
            <span>{upvotes}</span>
          </div>
          <div className="stat">
            <MessageCircle size={14} />
            <span>{comments}</span>
          </div> */}
        </div>
        <div className="signal-actions">
          <button
            className={`draft-reply-btn ${
              status === "replied" ? "replied" : "secondary"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              if (status !== "replied") {
                onMarkAsReplied();
              }
            }}
            disabled={status === "replied"}
          >
            <CheckCircle size={16} />
            {status === "replied" ? "Replied" : "Mark as Replied"}
          </button>
          <button
            className="draft-reply-btn"
            onClick={(e) => {
              e.stopPropagation();
              onDraftReply();
            }}
            disabled={isDraftLoading}
          >
            <Zap size={16} />
            {isDraftLoading ? "Generating…" : "Draft Reply"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SocialSignalCard;
